import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import { db, storage } from "../../FirebaseConfig";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Textarea from "@mui/joy/Textarea";
import AddIcon from "@mui/icons-material/Add";
import FormControl from "@mui/joy/FormControl";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import DeleteIcon from "@mui/icons-material/Delete";
import Radio from "@mui/joy/Radio";
import RadioGroup from "@mui/joy/RadioGroup";

interface RouteParams {
  [key: string]: string | undefined;
  id: string;
}

function parseSlug(slug: any): any {
  return slug
    .split("-")
    .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const RequiredLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <FormLabel>
    {children} <span style={{ color: "red" }}>*</span>
  </FormLabel>
);

// CTF Challenge structure
type CTFChallenge = {
  id: number;
  title: string;
  description: string;
  points: number;
  fileUrl: string;
  flag: string;
  category: string;
  difficulty: string;
};

type EventState = {
  id: string;
  eventname: string;
  eventdescription: string;
  eventrules: string;
  startdate: string;
  enddate: string;
  ctfChallenges: CTFChallenge[];
  fileUrl: string;
  category: string;
  organizer: string;
  live: boolean;
  prize: string;
  vpnconfiguration: string;
};

const initialState: EventState = {
  id: "",
  eventname: "",
  eventdescription: "",
  eventrules: "",
  startdate: "",
  enddate: "",
  ctfChallenges: [],
  fileUrl: "",
  category: "",
  organizer: "",
  vpnconfiguration: "",
  live: true,
  prize: "",
};

export default function EditEvent() {
  const { id } = useParams<RouteParams>();
  const [data, setData] = React.useState<EventState>(initialState);
  const [fileLoading, setFileLoading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [uploadFile, setUploadFile] = React.useState("");
  const [eventNameError, setEventNameError] = React.useState("");
  const [challengeinputType, setchallengeInputType] = React.useState("url");

  React.useEffect(() => {
    // Fetch the event data when the component mounts
    const fetchEventData = async () => {
      const eventDoc = await db
        .collection("Events")
        .doc(parseSlug(id).toLowerCase())
        .get();
      if (eventDoc.exists) {
        setData(eventDoc.data() as EventState);
        setUploadFile(eventDoc.data()?.fileUrl || "");
      } else {
        toast.error("Event not found");
      }
    };

    fetchEventData();
  }, [id]);

  const handleChallengeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setchallengeInputType(event.target.value as "file" | "url");
  };

  const handleChange = (e: any) => {
    if (!e || !e.target) {
      return;
    }
    const { id, name, value } = e.target;
    const key = id || name;

    if (key === "eventname") {
      if (value.length > 50) {
        setEventNameError("Event name must not exceed 50 characters");
      } else {
        setEventNameError("");
      }
    }

    setData((prevData: any) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const handleCTFChallengeChange = (
    id: number,
    event: any,
    field: keyof CTFChallenge
  ) => {
    const newChallenges = data.ctfChallenges.map((challenge) => {
      if (challenge.id === id) {
        return { ...challenge, [field]: event.target.value };
      }
      return challenge;
    });
    setData((prevData) => ({ ...prevData, ctfChallenges: newChallenges }));
  };

  const addCTFChallenge = () => {
    const newId = data.ctfChallenges.length + 1;
    const newChallenges = [
      ...data.ctfChallenges,
      {
        id: newId,
        title: "",
        description: "",
        points: 0,
        fileUrl: "",
        flag: "",
        category: "",
        difficulty: "",
      },
    ];
    setData((prev) => ({ ...prev, ctfChallenges: newChallenges }));
  };

  const handleCatSelectChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null,
    id: number
  ) => {
    if (newValue) {
      setData((prevData: any) => ({
        ...prevData,
        ctfChallenges: prevData.ctfChallenges.map((challenge: any) =>
          challenge.id === id ? { ...challenge, category: newValue } : challenge
        ),
      }));
    }
  };

  const handleDiffSelectChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null,
    id: number
  ) => {
    if (newValue) {
      setData((prevData: any) => ({
        ...prevData,
        ctfChallenges: prevData.ctfChallenges.map((challenge: any) =>
          challenge.id === id
            ? { ...challenge, difficulty: newValue }
            : challenge
        ),
      }));
    }
  };

  const removeCTFChallenge = (id: number) => {
    const newChallenges = data.ctfChallenges.filter(
      (challenge) => challenge.id !== id
    );
    setData((prevData) => ({
      ...prevData,
      ctfChallenges: newChallenges,
    }));
  };

  const handlePostUrl = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploadFile(event.target.value);
  };

  const handlePostFile = async (
    e: any,
    type: "banner" | "challenge",
    challengeId?: number
  ) => {
    setFileLoading(true);
    const file = e.target.files[0];

    // If uploading a banner, check that the file is an image
    if (type === "banner" && !file.type.startsWith("image/")) {
      setFileLoading(false);
      toast.error("Please upload an image file for the event banner.");
      return;
    }

    const eventName = data.eventname;
    const storageRef = storage.ref();
    const fileRef = storageRef.child(`Event-files/${eventName}/${file.name}`);
    const uploadTask = fileRef.put(file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {
        toast.error("Upload failed!");
      },
      () => {
        uploadTask.snapshot.ref.getDownloadURL().then((downloadURL) => {
          setFileLoading(false);

          if (type === "challenge") {
            setData((prevData) => ({
              ...prevData,
              ctfChallenges: prevData.ctfChallenges.map((challenge) =>
                challenge.id === challengeId
                  ? { ...challenge, fileUrl: downloadURL }
                  : challenge
              ),
            }));
          } else {
            setUploadFile(downloadURL);
          }

          toast.success("File Uploaded Successfully");
        });
      }
    );
  };

  // Validation function
  const validateForm = () => {
    return (
      data.eventname &&
      data.eventname.length <= 50 &&
      data.eventdescription &&
      data.eventrules &&
      data.startdate &&
      data.enddate &&
      data.organizer &&
      data.prize &&
      (data.category === "CTF"
        ? data.ctfChallenges.every(
          (c) => c.title && c.description && c.points && c.flag
        )
        : data.category === "Bug Bounty" && data.vpnconfiguration) &&
      (!uploadFile || !fileLoading)
    );
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    data.fileUrl = uploadFile;

    if (!validateForm()) {
      toast.error("Please fill out all required fields.");
      return;
    }

    db.collection("Events")
      .doc(parseSlug(id).toLowerCase())
      .update({
        eventname: data.eventname,
        eventdescription: data.eventdescription,
        eventrules: data.eventrules,
        startdate: data.startdate,
        enddate: data.enddate,
        ctfChallenges: data.ctfChallenges,
        fileUrl: data.fileUrl,
        category: data.category,
        organizer: data.organizer,
        live: data.live,
        vpnconfiguration: data.vpnconfiguration,
        prize: data.prize,
      })
      .then(() => {
        toast.success("Event Updated Successfully");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      });
  };

  const renderEventFields = () => {
    if (data.category === "Bug Bounty") {
      return (
        <>
          <RequiredLabel>
            <FormLabel> Bug Bounty Event Name</FormLabel>
          </RequiredLabel>
          <Input
            size="sm"
            id="eventname"
            onChange={handleChange}
            value={data.eventname}
            placeholder="Enter the name of the event."
            required
            error={!!eventNameError}
          />
          {eventNameError && (
            <Typography level="body-xs" color="danger">
              {eventNameError}
            </Typography>
          )}
          <RequiredLabel>
            <FormLabel> Bug Bounty Event Description </FormLabel>
          </RequiredLabel>
          <Textarea
            size="sm"
            name="eventdescription"
            onChange={handleChange}
            value={data.eventdescription}
            placeholder="Provide a detailed description of the event"
            required
          />

          <RequiredLabel>
            <FormLabel>
              {" "}
              Bug Bounty Event Rules (Note: Use Comma to separate each rule)
            </FormLabel>
          </RequiredLabel>
          <Textarea
            size="sm"
            name="eventrules"
            onChange={handleChange}
            value={data.eventrules}
            placeholder="Provide the rules of the event"
            required
          />

          <Stack direction="row" spacing={2}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <RequiredLabel>
                <FormLabel> Start Date </FormLabel>
              </RequiredLabel>
              <Input
                size="sm"
                type="datetime-local"
                id="startdate"
                onChange={handleChange}
                value={data.startdate}
                required
              />
            </Box>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <RequiredLabel>
                <FormLabel> End Date </FormLabel>
              </RequiredLabel>
              <Input
                size="sm"
                type="datetime-local"
                id="enddate"
                onChange={handleChange}
                value={data.enddate}
                required
              />
            </Box>
          </Stack>

          <RequiredLabel>
            <FormLabel>
              {" "}
              VPN Configuration Guide (Note: Use Comma to separate each step)
            </FormLabel>
          </RequiredLabel>
          <Textarea
            size="sm"
            name="vpnconfiguration"
            onChange={handleChange}
            value={data.vpnconfiguration}
            placeholder="Provide the steps to configure the VPN"
            required
          />
          <RequiredLabel>
            <FormLabel> Event Organizer </FormLabel>
          </RequiredLabel>
          <Input
            size="sm"
            id="organizer"
            onChange={handleChange}
            value={data.organizer}
            placeholder="Enter the name of the event organizer."
            required
          />
          <RequiredLabel>
            <FormLabel> Prize </FormLabel>
          </RequiredLabel>
          <Input
            size="sm"
            id="prize"
            onChange={handleChange}
            value={data.prize}
            placeholder="Enter the prize for the event."
            required
          />
          <RequiredLabel>
            <FormLabel> Upload Bug Bounty Event Banner </FormLabel>
          </RequiredLabel>
          <Input
            size="sm"
            type="file"
            onChange={(e) => handlePostFile(e, "banner")}
            required
          />
        </>
      );
    } else if (data.category === "CTF") {
      return (
        <>
          <RequiredLabel> CTF Event Name</RequiredLabel>
          <Input
            size="sm"
            id="eventname"
            value={data.eventname || ""}
            onChange={handleChange}
            placeholder="Enter the name of the event."
            required
            error={!!eventNameError}
          />
          {eventNameError && (
            <Typography level="body-xs" color="danger">
              {eventNameError}
            </Typography>
          )}

          <RequiredLabel> CTF Event Description </RequiredLabel>
          <Textarea
            size="sm"
            name="eventdescription"
            value={data.eventdescription || ""}
            onChange={handleChange}
            placeholder="Provide a detailed description of the event"
            required
          />

          <RequiredLabel> CTF Event Rules </RequiredLabel>
          <Textarea
            size="sm"
            name="eventrules"
            value={data.eventrules || ""}
            onChange={handleChange}
            placeholder="Provide the rules of the event"
            required
          />

          <Stack direction="row" spacing={2}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <RequiredLabel>
                <FormLabel> Start Date </FormLabel>
              </RequiredLabel>
              <Input
                size="sm"
                type="datetime-local"
                id="startdate"
                value={data.startdate}
                onChange={handleChange}
                required
              />
            </Box>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <RequiredLabel>
                <FormLabel> End Date </FormLabel>
              </RequiredLabel>
              <Input
                size="sm"
                type="datetime-local"
                id="enddate"
                value={data.enddate}
                onChange={handleChange}
                required
              />
            </Box>
          </Stack>

          {data.ctfChallenges.map((challenge, index) => (
            <Stack spacing={2} sx={{ my: 2 }}>
              <Box key={challenge.id} sx={{ mb: 3 }}>
                <Typography level="h4" sx={{ mb: 1 }}>
                  Challenge {index + 1}
                </Typography>
                <RequiredLabel>
                  <FormLabel>Challenge Title</FormLabel></RequiredLabel>
                <Input
                  size="sm"
                  value={challenge.title}
                  onChange={(e) =>
                    handleCTFChallengeChange(challenge.id, e, "title")
                  }
                  placeholder="Enter challenge title"
                  sx={{ mb: 2 }}
                  required
                />

                <RequiredLabel><FormLabel>Challenge Description</FormLabel></RequiredLabel>
                <Textarea
                  size="sm"
                  value={challenge.description}
                  onChange={(e) =>
                    handleCTFChallengeChange(challenge.id, e, "description")
                  }
                  placeholder="Enter challenge description"
                  sx={{ mb: 2 }}
                  required
                />

                <RequiredLabel>
                  <FormLabel>Points</FormLabel>
                </RequiredLabel>
                <Input
                  size="sm"
                  type="number"
                  value={challenge.points}
                  onChange={(e) =>
                    handleCTFChallengeChange(challenge.id, e, "points")
                  }
                  placeholder="Enter challenge points"
                  required
                  sx={{ mb: 2 }}
                />

                <RequiredLabel>
                  <FormLabel>Flag</FormLabel>
                </RequiredLabel>
                <Input
                  size="sm"
                  value={challenge.flag}
                  onChange={(e) =>
                    handleCTFChallengeChange(challenge.id, e, "flag")
                  }
                  placeholder="Enter challenge flag"
                  required
                  sx={{ mb: 2 }}
                />

                <RequiredLabel>
                  <FormLabel> Challenge Category </FormLabel>
                </RequiredLabel>
                <Select
                  id="category"
                  size="sm"
                  value={challenge.category}
                  onChange={(e, value) =>
                    handleCatSelectChange(
                      e,
                      value as string | null,
                      challenge.id
                    )
                  }
                  placeholder="Choose the Category of Challenge"
                  required
                  sx={{ mb: 2 }}
                >
                  <Option value="Web Exploitation">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Web Exploitation
                    </Typography>
                  </Option>
                  <Option value="Digital Forensics">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Digital Forensics
                    </Typography>
                  </Option>
                  <Option value="Cryptography">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Cryptography
                    </Typography>
                  </Option>
                  <Option value="Steganography">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Steganography
                    </Typography>
                  </Option>
                  <Option value="Reverse Engineering">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Reverse Engineering
                    </Typography>
                  </Option>
                  <Option value="Miscellaneous">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Miscellaneous
                    </Typography>
                  </Option>
                  <Option value="OSINT">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      OSINT
                    </Typography>
                  </Option>
                  <Option value="Binary Exploitation">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Binary Exploitation
                    </Typography>
                  </Option>
                  <Option value="Networking">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Networking
                    </Typography>
                  </Option>
                  <Option value="OT">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      OT
                    </Typography>
                  </Option>
                </Select>

                <RequiredLabel>
                  <FormLabel>Difficulty</FormLabel>
                </RequiredLabel>
                <Select
                  id="difficulty"
                  size="sm"
                  value={challenge.difficulty}
                  onChange={(e, value) =>
                    handleDiffSelectChange(
                      e,
                      value as string | null,
                      challenge.id
                    )
                  }
                  placeholder="Choose the Difficulty of Challenge"
                  required
                >
                  <Option value="Easy">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Easy
                    </Typography>
                  </Option>
                  <Option value="Moderate">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Moderate
                    </Typography>
                  </Option>
                  <Option value="Hard">
                    <Typography textColor="text.tertiary" ml={0.5}>
                      Hard
                    </Typography>
                  </Option>
                </Select>

                <Stack spacing={2} sx={{ my: 1 }}>
                  <FormLabel>Challenge File Type</FormLabel>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "center" }}>
                    <RadioGroup
                      orientation="horizontal"
                      aria-labelledby="file-type-radio"
                      name="challengeinputType"
                      value={challengeinputType}
                      onChange={handleChallengeInputChange}
                      sx={{
                        minHeight: 48,
                        padding: "4px",
                        borderRadius: "12px",
                        bgcolor: "neutral.softBg",
                        "--RadioGroup-gap": "4px",
                        "--Radio-actionRadius": "8px",
                      }}
                    >
                      {["file", "url"].map((item) => (
                        <Radio
                          key={item}
                          color="neutral"
                          value={item}
                          disableIcon
                          label={item}
                          variant="plain"
                          sx={{ px: 2, alignItems: "center" }}
                          slotProps={{
                            action: ({ checked }) => ({
                              sx: {
                                ...(checked && {
                                  bgcolor: "background.surface",
                                  boxShadow: "sm",
                                  "&:hover": { bgcolor: "background.surface" },
                                }),
                              },
                            }),
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </Box>
                  {challengeinputType === "file" ? (
                    <>
                      <FormLabel>Upload Challenge File</FormLabel>
                      <Input
                        size="sm"
                        type="file"
                        onChange={(e) =>
                          handlePostFile(e, "challenge", challenge.id)
                        }
                        required={!challenge.fileUrl} // only required if no file URL is provided
                      />
                    </>
                  ) : (
                    <>
                      <FormLabel>Post URL for the Challenge File</FormLabel>
                      <Input
                        size="sm"
                        onChange={handlePostUrl}
                        value={challenge.fileUrl || ""}
                        placeholder="URL for the Challenge File"
                        required={!challenge.fileUrl} // only required if no file is uploaded
                      />
                    </>
                  )}
                </Stack>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  <Button
                    size="sm"
                    variant="plain"
                    color="danger"
                    startDecorator={<DeleteIcon />}
                    onClick={() => removeCTFChallenge(challenge.id)}
                  >
                    Remove Challenge
                  </Button>
                </Box>
              </Box>
            </Stack>
          ))}

          <Button
            size="sm"
            variant="plain"
            color="primary"
            startDecorator={<AddIcon />}
            onClick={addCTFChallenge}
          >
            Add CTF Challenge
          </Button>

          <RequiredLabel>
            <FormLabel> Event Organizer </FormLabel>
          </RequiredLabel>
          <Input
            size="sm"
            id="organizer"
            onChange={handleChange}
            placeholder="Enter the name of the event organizer."
            value={data.organizer}
            required
          />
          <RequiredLabel>
            <FormLabel> Prize </FormLabel>
          </RequiredLabel>
          <Input
            size="sm"
            id="prize"
            onChange={handleChange}
            placeholder="Enter the prize for the event."
            value={data.prize}
            required
          />
          <RequiredLabel>
            <FormLabel> Upload CTF Event Banner </FormLabel>
          </RequiredLabel>
          <Input
            size="sm"
            type="file"
            onChange={(e) => handlePostFile(e, "banner")}
            required
          />
        </>
      );
    }

    return null;
  };

  return (
    <Box sx={{ flex: 1, width: "100%" }}>
      <Stack
        spacing={4}
        sx={{
          display: "flex",
          mx: "auto",
          py: { xs: 2, md: 3 },
        }}
      >
        <Card>
          <Box sx={{ mb: 1 }}>
            <Typography level="title-md">Edit Event</Typography>
            <Typography level="body-sm">Edit the required fields</Typography>
          </Box>
          <Divider />
          <Stack direction="row" spacing={3}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              {renderEventFields()}
            </Stack>
          </Stack>
          {fileLoading && <h4>Uploading File {progress.toFixed(2)} %</h4>}
          <CardOverflow sx={{ borderTop: "1px solid", borderColor: "divider" }}>
            <CardActions sx={{ alignSelf: "flex-end", pt: 2 }}>
              <Button
                component={Link}
                to="/dashboard"
                size="sm"
                variant="outlined"
                color="neutral"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="outlined"
                onClick={onSubmit}
                disabled={!validateForm()}
              >
                Save
              </Button>
            </CardActions>
          </CardOverflow>
        </Card>
      </Stack>
    </Box>
  );
}
